import Currency from '@/common/currency'
import { Interface } from '@/shared/di/interface'
import { ISize } from '@/shared/di/interface/size'
import globalRenderer from '@/shared/misc/global-renderer'
import { Asset, TokenType } from '@/state/vault/domain'

import {
  AdditiveBlending,
  Clock,
  Color,
  CubeCamera,
  DoubleSide,
  Euler,
  Group,
  HalfFloatType,
  InstancedMesh,
  LinearEncoding,
  LinearMipMapLinearFilter,
  LinearToneMapping,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
  MeshStandardMaterial,
  NoToneMapping,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  RawShaderMaterial,
  RingGeometry,
  Scene,
  ShadowMaterial,
  SphereGeometry,
  SpotLight,
  Texture,
  Vector2,
  Vector3,
  WebGLCubeRenderTarget,
  WebGLRenderer
} from 'three'
import { container } from 'tsyringe'
import CoinController from '../experience/coin-controller'
import { SpawnLocation } from '../experience/misc/constants'
import PostEffectManager from '../experience/posteffects/posteffects-manager'
import domeVS from './shaders/dome-vs.glsl'
import domeFS from './shaders/dome-fs.glsl'
import billboardLightVS from './shaders/billboardlight-vs.glsl'
import billboardLightFS from './shaders/billboardlight-fs.glsl'
import ringVS from './shaders/ring-vs.glsl'
import ringFS from './shaders/ring-fs.glsl'
import studioVS from './shaders/studio-vs.glsl'
import studioFS from './shaders/studio-fs.glsl'
import smallSpotLightVS from './shaders/small-spotlight-vs.glsl'
import smallSpotLightFS from './shaders/small-spotlight-fs.glsl'
import { IDebug } from '@/shared/di/interface/debug'
import Action from '@/common/action'
import { DebugFlag, Platform } from '@/common/constants'
import { FolderApi } from 'tweakpane/dist/types/blade/folder/api/folder'
import TingController from '../experience/ting-controller'
import BaseController from '../experience/base-controller'
import { IAssets } from '@/shared/di/interface/assets'

const WALL_WIDTH = 40
const WALL_HEIGHT = 40
const MAX_SMALL_SPOTLIGHTS = 2000

let latestTexture: Texture | undefined
let latestSettingsAsString = ''

const envMapRenderTarget = new WebGLCubeRenderTarget(256, {
  //generateMipmaps: false,
  //minFilter: LinearFilter,
  generateMipmaps: true,
  minFilter: LinearMipMapLinearFilter,
  encoding: LinearEncoding,
  type: HalfFloatType
})
export default class EnvironmentEditor {
  onEnvMapUpdated = new Action<void>()

  private previewImageEl: HTMLImageElement | undefined
  private isEditor: boolean
  private scene: Scene
  private previewScene: Scene
  private camera: PerspectiveCamera
  private previewCamera: PerspectiveCamera
  private renderer: WebGLRenderer

  private envMapDirty = true
  private backgroundGroup = new Group()
  private coins: BaseController[] = []
  private time = 0
  private clock = new Clock()
  private pmremGenerator: PMREMGenerator
  private cubeCamera: CubeCamera
  private postEffectManager: PostEffectManager | undefined
  private envMapObjectContainer: Group
  private billboardLights: Group[] = []
  private billboardLightMaterial: RawShaderMaterial = {} as RawShaderMaterial

  private spotLight: SpotLight
  private useDebugWalls = false
  private showBackground = false
  private settingsAsString = ''

  private defaultSettings: any = {
    useDome: true,
    groundColor: '#333333',
    skyIntensity: 0.6,
    skyColor: '#999999',
    globalIntensity: 0.5,
    verticalGradientFrequency: 4,
    verticalGradientIntensity: 0.8,
    verticalGradientOffset: 0.2,
    horizontalGradientFrequency: 0,
    horizontalGradientIntensity: 1.2,
    horizontalGradientOffset: 0,

    billboardLightCount: 10,
    billboardLightOffset: 3.02,
    billboardLightColor: '#ffffff',
    billboardLightIntensity: 3,
    billboardLightRotation: -0.5,

    walls: {
      roof: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#ff00ff' },
      floor: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#ffff00' },
      left: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#ff0000' },
      right: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#0000ff' },
      front: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#ffffff' },
      back: { use: false, color: '#ffffff', intensity: 1, scale: { x: 1, y: 1 }, debugColor: '#00ffff' }
    },

    wallsSize: WALL_WIDTH,

    useSmallSpotLights: true,
    smallSpotLightsCount: 1000,
    smallSpotLightsIntensity: 0.05,
    smallSpotLightsScale: 4,
    smallSpotLightsColor: '#736ef9'
  }

  private activeSettings: any = JSON.parse(JSON.stringify(this.defaultSettings))

  private domeMesh: Mesh | undefined
  private domeMaterial: RawShaderMaterial = {} as RawShaderMaterial

  private sideMeshesMap: Map<string, Mesh> = new Map()

  private smallSpotLights: InstancedMesh | undefined
  private smallSpotLightsMaterial: RawShaderMaterial = {} as RawShaderMaterial

  private tweakpaneRootFolder: FolderApi | undefined

  /* private denominations = [
    100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000,
    10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000, 200000, 300000, 400000, 500000, 600000,
    700000, 800000, 900000
  ]*/

  private denominations = [
    10, 20, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000,
    10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000
  ]

  private currentDenominationIndex = this.denominations.length - 5

  constructor(isEditor = false) {
    this.isEditor = isEditor
    //this.activeSettings = this.debugSettings
    this.renderer = globalRenderer.getRenderer()
    this.camera = new PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 200)
    this.camera.position.z = 20
    this.scene = new Scene()

    this.previewCamera = new PerspectiveCamera(40, 512 / 256, 0.1, 200)
    this.previewCamera.position.z = 5
    this.previewScene = new Scene()

    this.spotLight = new SpotLight()
    this.spotLight.angle = Math.PI * 0.2
    this.spotLight.castShadow = true
    this.spotLight.position.y = 20
    this.spotLight.position.z = 25
    this.scene.add(this.spotLight)

    this.envMapObjectContainer = new Group()
    //this.envMapObjectContainer.layers.enable(0)

    this.envMapObjectContainer.userData.enableBloom = false
    this.scene.add(this.envMapObjectContainer)

    this.postEffectManager = new PostEffectManager(this.renderer, true)
    this.postEffectManager.setBloomAmount(0.5)
    this.postEffectManager.setSceneAndCamera(this.scene, this.camera)

    this.pmremGenerator = new PMREMGenerator(this.renderer)
    this.pmremGenerator.compileCubemapShader()

    this.cubeCamera = new CubeCamera(0.1, 100, envMapRenderTarget)
    this.cubeCamera.position.y = 0
    this.camera.layers.disable(2)
    this.cubeCamera.layers.enable(2)

    this.createSmallSpotlights()
    this.createBillboards()
    this.createDome()
    this.createStudioBackground()

    //this.createMainSpotLight()

    this.createSide('front', new Vector3(0, 0, 1), new Euler(0, 0, 0))
    this.createSide('back', new Vector3(0, 0, -1), new Euler(0, 0, 0))
    this.createSide('roof', new Vector3(0, 1, 0), new Euler(Math.PI * -0.5, 0, 0))
    this.createSide('floor', new Vector3(0, -1, 0), new Euler(Math.PI * -0.5, 0, 0))
    this.createSide('left', new Vector3(-1, 0, 0), new Euler(0, Math.PI * -0.5, 0))
    this.createSide('right', new Vector3(1, 0, 0), new Euler(0, Math.PI * 0.5, 0))

    this.envMapObjectContainer.traverse(child => child.layers.set(2))

    if (this.isEditor) {
      const size = container.resolve<ISize>(Interface.ISize)
      size.onResize.add(this.resize)
      this.resize()
      this.createSettingsController()
      this.createEditorToolsSettings()
      this.createGenericToolsSettings()
      this.startEditor()
      this.stepThroughModels(1)
    }

    this.envMapDirty = true
    this.renderEnvMap()
    this.updatePreview()
  }

  createStudioBackground() {
    const assets = container.resolve<IAssets>(Interface.IAssets)

    this.scene.add(this.backgroundGroup)

    const modelRoot = assets.getModel('studio')
    if (modelRoot) {
      const mesh = modelRoot.children[0].clone() as Mesh
      mesh.receiveShadow = false
      mesh.userData.enableBloom = false
      mesh.scale.setScalar(50)
      mesh.position.y = -5
      mesh.position.z = -5

      const material = new RawShaderMaterial({
        uniforms: {
          color: { value: new Color(0x305460).convertSRGBToLinear() },
          colorFloor: { value: new Color(0x91c6d1).convertSRGBToLinear() }
        },
        vertexShader: studioVS,
        fragmentShader: studioFS
      })

      mesh.material = material

      this.backgroundGroup.add(mesh)
    }

    const shadowMesh = new Mesh(
      new PlaneGeometry(100, 100, 1, 1),
      new ShadowMaterial({ opacity: 0.3, fog: false, depthTest: false, depthWrite: true })
    )

    shadowMesh.rotation.x = Math.PI * -0.5

    shadowMesh.position.y = -6
    shadowMesh.receiveShadow = true
    shadowMesh.userData.enableBloom = false
    this.backgroundGroup.add(shadowMesh)
  }

  createGenericToolsSettings() {}

  createEditorToolsSettings() {
    const debug = container.resolve<IDebug>(Interface.IDebug)
    const rootFolder = debug.pane.addFolder({ title: 'Editor Tools', expanded: false })

    rootFolder.addButton({ title: 'Prev coin' }).on('click', () => {
      this.stepThroughModels(-1)
    })

    rootFolder.addButton({ title: 'Next coin' }).on('click', () => {
      this.stepThroughModels(1)
    })

    rootFolder.addButton({ title: 'Red heart' }).on('click', () => {
      this.removeAllModels()
      this.createTing(Currency.HEART, 'red')
    })

    rootFolder.addButton({ title: 'Gold heart' }).on('click', () => {
      this.removeAllModels()
      this.createTing(Currency.HEART, 'gold')
    })

    rootFolder.addInput(this, 'showBackground', { label: 'Show environment' }).on('change', () => {
      this.envMapObjectContainer.traverse(child => {
        if (this.showBackground) {
          child.layers.enable(0)
          child.userData.enableBloom = false
        } else {
          child.layers.disable(0)
        }

        child.layers.enable(2)
      })
    })

    debug.pane.expanded = this.isEditor
  }

  stepThroughModels(step: number) {
    this.currentDenominationIndex += step

    if (this.currentDenominationIndex > this.denominations.length - 1) {
      this.currentDenominationIndex = 0
    } else if (this.currentDenominationIndex < 0) {
      this.currentDenominationIndex = this.denominations.length - 1
    }

    const d = this.denominations[this.currentDenominationIndex]

    this.removeAllModels()
    this.createCoin(d, 0, 0, true)
  }

  removeAllModels() {
    this.coins.forEach(cc => {
      this.scene.remove(cc.mesh)
      cc.destroy()
    })

    this.coins.length = 0
  }

  createSettingsController() {
    const debug = container.resolve<IDebug>(Interface.IDebug)

    const rootFolder = debug.pane.addFolder({ title: 'Environment', expanded: true })

    debug.pane.expanded = this.isEditor

    this.tweakpaneRootFolder = rootFolder

    const domeFolder = rootFolder.addFolder({ title: 'Background', expanded: true })

    const billboardLightsFolder = rootFolder.addFolder({ title: 'Billboards', expanded: false })
    const wallsFolder = rootFolder.addFolder({ title: 'Walls', expanded: false })

    const smallSpotLightsFolder = rootFolder.addFolder({ title: 'Small spotlights', expanded: false })

    //dome
    domeFolder.addInput(this.activeSettings, 'useDome', { label: 'Use' }).on('change', x => {
      this.domeMesh!.visible = this.activeSettings.useDome
      this.envMapDirty = true
      this.renderEnvMap()
    })
    domeFolder.addInput(this.activeSettings, 'groundColor').on('change', x => {
      this.domeMaterial.uniforms.groundColor.value.set(this.activeSettings.groundColor)
      this.envMapDirty = true
      this.renderEnvMap()
    })
    domeFolder.addInput(this.activeSettings, 'skyColor').on('change', x => {
      this.domeMaterial.uniforms.skyColor.value.set(this.activeSettings.skyColor)
      this.envMapDirty = true
      this.renderEnvMap()
    })
    domeFolder.addInput(this.activeSettings, 'skyIntensity').on('change', x => {
      this.domeMaterial.uniforms.skyIntensity.value = this.activeSettings.skyIntensity
      this.envMapDirty = true
      this.renderEnvMap()
    })
    domeFolder.addInput(this.activeSettings, 'globalIntensity', { min: 0, max: 1 }).on('change', x => {
      this.domeMaterial.uniforms.globalIntensity.value = this.activeSettings.globalIntensity
      this.envMapDirty = true
      this.renderEnvMap()
    })

    const domeTabs = domeFolder.addTab({
      pages: [{ title: 'Horisontal' }, { title: 'Vertical' }]
    })

    domeTabs.pages[0]
      .addInput(this.activeSettings, 'horizontalGradientFrequency', { min: 0, max: 40, label: 'Freq' })
      .on('change', x => {
        this.domeMaterial.uniforms.horizontalGradientFrequency.value = this.activeSettings.horizontalGradientFrequency
        this.envMapDirty = true
        this.renderEnvMap()
      })
    domeTabs.pages[0]
      .addInput(this.activeSettings, 'horizontalGradientIntensity', { min: 0, max: 4, label: 'Intensity' })
      .on('change', x => {
        this.domeMaterial.uniforms.horizontalGradientIntensity.value = this.activeSettings.horizontalGradientIntensity
        this.envMapDirty = true
        this.renderEnvMap()
      })
    domeTabs.pages[0]
      .addInput(this.activeSettings, 'horizontalGradientOffset', { min: 0, max: 1, label: 'Offset' })
      .on('change', x => {
        this.domeMaterial.uniforms.horizontalGradientOffset.value = this.activeSettings.horizontalGradientOffset
        this.envMapDirty = true
        this.renderEnvMap()
      })

    domeTabs.pages[1]
      .addInput(this.activeSettings, 'verticalGradientFrequency', { min: 0, max: 10, label: 'Freq' })
      .on('change', x => {
        this.domeMaterial.uniforms.verticalGradientFrequency.value = this.activeSettings.verticalGradientFrequency
        this.envMapDirty = true
        this.renderEnvMap()
      })
    domeTabs.pages[1]
      .addInput(this.activeSettings, 'verticalGradientIntensity', { min: 0, max: 4, label: 'Intensity' })
      .on('change', x => {
        this.domeMaterial.uniforms.verticalGradientIntensity.value = this.activeSettings.verticalGradientIntensity
        this.envMapDirty = true
        this.renderEnvMap()
      })
    domeTabs.pages[1]
      .addInput(this.activeSettings, 'verticalGradientOffset', { min: 0, max: 1, label: 'Offset' })
      .on('change', x => {
        this.domeMaterial.uniforms.verticalGradientOffset.value = this.activeSettings.verticalGradientOffset
        this.envMapDirty = true
        this.renderEnvMap()
      })

    billboardLightsFolder
      .addInput(this.activeSettings, 'billboardLightCount', { min: 0, max: 20, step: 1, label: 'Count' })
      .on('change', x => {
        this.updateBillboards()
        this.envMapDirty = true
        this.renderEnvMap()
      })
    billboardLightsFolder.addInput(this.activeSettings, 'billboardLightColor', { label: 'Color' }).on('change', x => {
      this.billboardLightMaterial.uniforms.color.value.set(this.activeSettings.billboardLightColor)
      this.envMapDirty = true
      this.renderEnvMap()
    })
    billboardLightsFolder
      .addInput(this.activeSettings, 'billboardLightIntensity', { min: 0, max: 10, label: 'Intensity' })
      .on('change', x => {
        this.billboardLightMaterial.uniforms.intensity.value = this.activeSettings.billboardLightIntensity
        this.envMapDirty = true
        this.renderEnvMap()
      })
    billboardLightsFolder
      .addInput(this.activeSettings, 'billboardLightOffset', { min: 0, max: Math.PI * 2, label: 'Offset' })
      .on('change', x => {
        this.updateBillboards()
        this.envMapDirty = true
        this.renderEnvMap()
      })
    billboardLightsFolder
      .addInput(this.activeSettings, 'billboardLightRotation', { min: 0, max: Math.PI * 2, label: 'Rotation' })
      .on('change', x => {
        this.updateBillboards()
        this.envMapDirty = true
        this.renderEnvMap()
      })

    wallsFolder.addInput(this, 'useDebugWalls', { label: 'Debug colors' }).on('change', () => {
      Object.keys(this.activeSettings.walls).forEach((key, index) => {
        const settingsObj = this.activeSettings.walls[key]
        const mesh = this.sideMeshesMap.get(key)!

        if (this.useDebugWalls) {
          ;(mesh.material as MeshLambertMaterial).emissive.set(settingsObj.debugColor)
          ;(mesh.material as MeshLambertMaterial).emissiveIntensity = 1
          mesh.visible = true
        } else {
          mesh.visible = settingsObj.use
          ;(mesh.material as MeshLambertMaterial).emissive.set(settingsObj.color)
          ;(mesh.material as MeshLambertMaterial).emissiveIntensity = settingsObj.intensity
        }

        wallsFolder.children.forEach((folder, index) => {
          if (index > 0) {
            folder.disabled = this.useDebugWalls
          }
        })
      })

      this.envMapDirty = true
      this.renderEnvMap()
    })

    Object.keys(this.activeSettings.walls).forEach((key, index) => {
      const sideFolder = wallsFolder.addFolder({ title: key, expanded: false })
      const setting = this.activeSettings.walls[key]

      const sideMesh = this.sideMeshesMap.get(key)!
      const sideMaterial = sideMesh!.material as MeshLambertMaterial

      sideFolder.addInput(setting, 'use', { label: 'Use' }).on('change', x => {
        sideMesh.visible = setting.use
        this.envMapDirty = true
        this.renderEnvMap()
      })
      sideFolder.addInput(setting, 'color', { label: 'Color' }).on('change', x => {
        sideMaterial.emissive.set(setting.color)
        this.envMapDirty = true
        this.renderEnvMap()
      })
      sideFolder.addInput(setting, 'intensity', { label: 'Intensity', min: 0, max: 4 }).on('change', x => {
        sideMaterial.emissiveIntensity = setting.intensity
        this.envMapDirty = true
        this.renderEnvMap()
      })
      sideFolder.addInput(setting, 'scale', { label: 'Scale', min: 0.1, max: 4 }).on('change', x => {
        sideMesh.scale.set(setting.scale.x, setting.scale.y, 1)
        this.envMapDirty = true
        this.renderEnvMap()
      })
    })

    smallSpotLightsFolder.addInput(this.activeSettings, 'useSmallSpotLights', { label: 'Use' }).on('change', x => {
      this.smallSpotLights!.visible = this.activeSettings.useSmallSpotLights
      this.envMapDirty = true
      this.renderEnvMap()
    })
    smallSpotLightsFolder.addInput(this.activeSettings, 'smallSpotLightsColor', { label: 'Color' }).on('change', x => {
      this.smallSpotLightsMaterial.uniforms.color.value.set(this.activeSettings.smallSpotLightsColor)
      this.envMapDirty = true
      this.renderEnvMap()
    })
    smallSpotLightsFolder
      .addInput(this.activeSettings, 'smallSpotLightsIntensity', { label: 'Intensity', min: 0, max: 3 })
      .on('change', x => {
        this.smallSpotLightsMaterial.uniforms.intensity.value = this.activeSettings.smallSpotLightsIntensity
        this.envMapDirty = true
        this.renderEnvMap()
      })

    smallSpotLightsFolder
      .addInput(this.activeSettings, 'smallSpotLightsScale', {
        min: 0.05,
        max: 10,
        label: 'Scale'
      })
      .on('change', x => {
        this.updateSmallSpotLights()
        this.envMapDirty = true
        this.renderEnvMap()
      })

    smallSpotLightsFolder
      .addInput(this.activeSettings, 'smallSpotLightsCount', {
        min: 0,
        max: MAX_SMALL_SPOTLIGHTS,
        step: 100,
        label: 'Count'
      })
      .on('change', x => {
        this.updateSmallSpotLights()
        this.envMapDirty = true
        this.renderEnvMap()
      })

    //preview
    const previewSphere = new Mesh(
      new SphereGeometry(1.6, 10, 10),
      new MeshStandardMaterial({
        color: 0xffffff,
        metalness: 1,
        roughness: 0,
        side: DoubleSide
      })
    )
    previewSphere.position.set(0, 0, 0)
    previewSphere.userData.enableBloom = false
    this.previewScene.add(previewSphere)

    this.previewImageEl = new Image()
    this.previewImageEl.style.width = '100%'
    this.previewImageEl.style.height = 'auto'
    rootFolder.controller_.view.containerElement.prepend(this.previewImageEl)
  }

  startEditor() {
    this.gameLoop()
  }

  createSmallSpotlights() {
    this.smallSpotLightsMaterial = new RawShaderMaterial({
      uniforms: {
        intensity: { value: this.activeSettings.smallSpotLightsIntensity },
        color: { value: new Color(this.activeSettings.smallSpotLightsColor) }
      },
      vertexShader: smallSpotLightVS,
      fragmentShader: smallSpotLightFS,
      side: DoubleSide,
      blending: AdditiveBlending,
      depthTest: false
    })

    const geo = new SphereGeometry(0.4, 15, 15)
    this.smallSpotLights = new InstancedMesh(geo, this.smallSpotLightsMaterial, MAX_SMALL_SPOTLIGHTS)
    this.smallSpotLights.visible = this.activeSettings.useSmallSpotLights
    this.smallSpotLights.renderOrder = 10
    const colorOff = new Color(0xffffff)

    const actorMatrix = new Matrix4()
    const pos = new Vector3()

    for (let index = 0; index < MAX_SMALL_SPOTLIGHTS; index++) {
      pos.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
      pos.normalize().multiplyScalar(30)
      actorMatrix.setPosition(pos.x, pos.y, pos.z)
      this.smallSpotLights.setMatrixAt(index, actorMatrix)
      this.smallSpotLights.setColorAt(index, colorOff)
    }

    this.updateSmallSpotLights()
    this.envMapObjectContainer.add(this.smallSpotLights)
  }

  updateSmallSpotLights() {
    const colorOn = new Color(0xffffff)
    const colorOff = new Color(0x000000)

    const currentMatrix = new Matrix4()
    const actorMatrix = new Matrix4()
    actorMatrix.makeScale(
      this.activeSettings.smallSpotLightsScale,
      this.activeSettings.smallSpotLightsScale,
      this.activeSettings.smallSpotLightsScale
    )

    for (let index = 0; index < MAX_SMALL_SPOTLIGHTS; index++) {
      this.smallSpotLights!.getMatrixAt(index, currentMatrix)

      actorMatrix.copyPosition(currentMatrix)

      this.smallSpotLights!.setMatrixAt(index, actorMatrix)
      this.smallSpotLights!.setColorAt(index, index < this.activeSettings.smallSpotLightsCount ? colorOn : colorOff)
    }

    this.smallSpotLights!.instanceColor!.needsUpdate = true
    this.smallSpotLights!.instanceMatrix!.needsUpdate = true
  }

  createBillboards() {
    this.billboardLightMaterial = new RawShaderMaterial({
      transparent: true,
      side: DoubleSide,
      uniforms: {
        intensity: { value: this.activeSettings.billboardLightIntensity },
        color: { value: new Color(this.activeSettings.billboardLightColor) }
      },
      vertexShader: billboardLightVS,
      fragmentShader: billboardLightFS
    })

    const stripCount = 20
    const geo = new PlaneGeometry(7, 100, 1, 1)
    for (let index = 0; index < stripCount; index++) {
      const strip = new Mesh(geo, this.billboardLightMaterial)

      strip.userData.enableBloom = true

      const stripContainer = new Group()
      stripContainer.add(strip)

      this.billboardLights.push(stripContainer)

      this.envMapObjectContainer.add(stripContainer)
    }

    this.updateBillboards()
  }

  updateBillboards() {
    this.billboardLights.forEach((stripGroup, index) => {
      if (index < this.activeSettings.billboardLightCount - 1) {
        stripGroup.visible = true

        const radius = 20
        const x =
          radius *
          Math.cos(
            (2 * Math.PI * index + this.activeSettings.billboardLightOffset) /
              (this.activeSettings.billboardLightCount - 1)
          )
        const z =
          radius *
          Math.sin(
            (2 * Math.PI * index + this.activeSettings.billboardLightOffset) /
              (this.activeSettings.billboardLightCount - 1)
          )

        stripGroup.position.set(x, 0, z)

        stripGroup.lookAt(this.scene.position)
        stripGroup.children[0]!.rotation.z = this.activeSettings.billboardLightRotation
      } else {
        stripGroup.visible = false
      }
    })
  }

  createDome() {
    this.domeMaterial = new RawShaderMaterial({
      transparent: false,
      side: DoubleSide,
      uniforms: {
        groundColor: { value: new Color(this.activeSettings.groundColor) },
        skyColor: { value: new Color(this.activeSettings.skyColor) },
        skyIntensity: { value: this.activeSettings.skyIntensity },
        globalIntensity: { value: this.activeSettings.globalIntensity },
        verticalGradientFrequency: { value: this.activeSettings.verticalGradientFrequency },
        verticalGradientIntensity: { value: this.activeSettings.verticalGradientIntensity },
        verticalGradientOffset: { value: this.activeSettings.verticalGradientOffset },

        horizontalGradientFrequency: { value: this.activeSettings.horizontalGradientFrequency },
        horizontalGradientIntensity: { value: this.activeSettings.horizontalGradientIntensity },
        horizontalGradientOffset: { value: this.activeSettings.horizontalGradientOffset }
      },
      vertexShader: domeVS,
      fragmentShader: domeFS,
      depthTest: false
    })

    this.domeMesh = new Mesh(new SphereGeometry(40, 15, 15), this.domeMaterial)
    this.domeMesh.visible = this.activeSettings.useDome
    this.domeMesh.userData.enableBloom = false
    this.envMapObjectContainer.add(this.domeMesh)
  }

  createSide(key: string, position: Vector3, rotation: Euler) {
    const setting = this.activeSettings.walls[key]
    const material = new MeshLambertMaterial({
      emissive: new Color(setting.color),
      emissiveIntensity: setting.intensity,
      side: DoubleSide
    })

    const mesh = new Mesh(new PlaneGeometry(WALL_WIDTH, WALL_HEIGHT, 1, 1), material)

    mesh.scale.set(setting.scale.x, setting.scale.y, 1)
    mesh.userData.enableBloom = false
    mesh.visible = setting.use
    this.envMapObjectContainer.add(mesh)
    mesh.position.copy(position).multiplyScalar(30)
    mesh.rotation.copy(rotation)

    this.sideMeshesMap.set(key, mesh)
  }

  createMainSpotLight() {
    const material = new RawShaderMaterial({
      transparent: true,
      side: DoubleSide,
      uniforms: {},
      vertexShader: ringVS,
      fragmentShader: ringFS,
      blending: AdditiveBlending
    })

    const mesh = new Mesh(new RingGeometry(0, 5, 16), material)
    mesh.position.z = -20
    mesh.position.x = 10
    mesh.userData.enableBloom = false
    this.envMapObjectContainer.add(mesh)
  }

  async createCoin(denomination: number, x: number, y: number, zoom = false) {
    const asset: Asset = {
      d: denomination,
      id: 'FakeCoin:3',
      c: Currency.TJIUSD,
      t: TokenType.Coin,
      ux: {
        user: {
          room: 'transfer',
          posx: 100,
          posy: -800
        }
      }
    } as Asset
    const cc = new CoinController(asset, SpawnLocation.InRoom)

    cc.isAnimated = false

    await cc.load()

    /*if (cc.asset.c === Currency.TJISEK) {
      cc.loadHighRes()
    }*/

    cc.enable()

    this.scene.add(cc.mesh)
    ;(cc.mesh.material as MeshStandardMaterial).metalness = 1
    ;(cc.mesh.material as MeshStandardMaterial).roughness = 0.04

    cc.setPosition(x, y)

    if (zoom) {
      cc.isAnimated = false
      cc.isZoomed = true
      cc.mesh.userData.enableBloom = true
      cc.addTouchListener()
    }

    this.postEffectManager!.updateBloomList()

    this.coins.push(cc)
  }

  async createTing(currency: Currency, variant: string, zoom = true) {
    const asset: Asset = {
      id: 'FakeTing:3',
      c: currency,
      va: variant,
      t: TokenType.Ting,
      ux: {
        user: {
          room: 'transfer'
        }
      }
    } as Asset
    const tc = new TingController(asset, SpawnLocation.InRoom)

    tc.isAnimated = false

    await tc.load()

    tc.enable()

    this.scene.add(tc.mesh)

    if (zoom) {
      tc.isAnimated = false
      tc.isZoomed = true
      tc.mesh.userData.enableBloom = true
      tc.addTouchListener()
    }

    this.postEffectManager!.updateBloomList()

    this.coins.push(tc)
  }

  resize = () => {
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    this.camera.aspect = (size.width - 310) / size.height
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(size.width - 310, size.height)
    this.postEffectManager?.resize()
  }

  updatePreview() {
    const debug = container.resolve<IDebug>(Interface.IDebug)
    if (debug.isDebug && (debug.getFlag(DebugFlag.Render) || this.isEditor)) {
      const oldSize = this.renderer.getSize(new Vector2())
      this.renderer.setSize(512, 256)
      this.renderer.toneMapping = LinearToneMapping
      this.renderer.render(this.previewScene, this.previewCamera)
      if (this.previewImageEl) {
        this.previewImageEl.src = this.renderer.domElement.toDataURL()
      }

      this.renderer.setSize(oldSize.x, oldSize.y)
      this.renderer.toneMapping = NoToneMapping
    }
  }

  getEnvMap(): Texture {
    const newSettings = JSON.stringify(this.activeSettings)

    if (newSettings === latestSettingsAsString && latestTexture) {
      return latestTexture
    }

    latestSettingsAsString = newSettings

    this.backgroundGroup.visible = false
    envMapRenderTarget.clear(this.renderer, true, true, true)
    this.cubeCamera.update(this.renderer, this.scene)
    latestTexture = this.pmremGenerator!.fromCubemap(envMapRenderTarget.texture).texture

    this.backgroundGroup.visible = true

    return latestTexture
  }

  renderEnvMap() {
    if (!this.envMapDirty) {
      return
    }
    this.envMapDirty = false

    this.coins.forEach(coin => (coin.mesh.visible = false))

    this.scene.environment = null

    this.backgroundGroup.visible = false

    envMapRenderTarget.clear(this.renderer, true, true, true)
    this.cubeCamera.update(this.renderer, this.scene)
    const envMap = this.pmremGenerator!.fromCubemap(envMapRenderTarget.texture).texture

    this.scene.environment = envMap
    this.previewScene.environment = envMap

    this.updatePreview()

    this.coins.forEach(coin => (coin.mesh.visible = true))
    this.backgroundGroup.visible = true

    this.onEnvMapUpdated.invoke()
  }

  private gameLoop = () => {
    requestAnimationFrame(this.gameLoop)

    this.update()
  }

  update() {
    let delta = this.clock.getDelta()

    if (delta > 1) {
      delta = 1
    }

    //this.envMapObjectContainer.rotation.y += 0.4 * delta
    //this.cubeCamera.rotation.y += 0.005 //Math.sin(this.time * 0.2)

    if (this.envMapDirty) {
      this.renderEnvMap()
    }

    this.time += delta

    if (this.postEffectManager) {
      this.postEffectManager.render(this.time, delta)
    }

    this.coins.forEach(coin => {
      coin.update(this.time, delta)
    })
  }

  destroy() {
    const size = container.resolve<ISize>(Interface.ISize)
    size.onResize.remove(this.resize)

    if (this.postEffectManager && this.postEffectManager.dispose) {
      this.postEffectManager.dispose()
    }

    this.tweakpaneRootFolder?.dispose()
  }
}
